import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, LayoutGrid, GripVertical } from "lucide-react";
import { DASHBOARD_WIDGETS, DASHBOARD_VIEWS, useDashboardConfig } from "@/lib/dashboardConfig";

// 149-cl: Dialog "Atur Widget" — sekarang per-view (Daily/Weekly/Monthly).
// Menampilkan hanya widget yang eligible untuk view aktif; reset hanya menyentuh
// view aktif; reset semua tersedia di footer.
export default function DashboardSettingsDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const { activeView, setActiveView, isVisible, toggleVisibility, moveUp, moveDown, moveTo, resetActiveView, reset, role, eligibleWidgets } = useDashboardConfig({ user, syncBackend: true });
  const [dragId, setDragId] = useState(null);

  // 149-cn: hook `useDashboardConfig` sudah mem-filter widget berdasarkan
  // views ∩ user permission. Kalau permission modul OFF → widget tidak muncul
  // di dialog Atur Widget & tidak melakukan fetch di DashboardPage.
  const orderedWidgets = eligibleWidgets
    .map((w) => ({ w, idx: DASHBOARD_WIDGETS.findIndex((x) => x.id === w.id) }))
    .sort((a, b) => a.idx - b.idx)
    .map((x) => x.w);

  const handleDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const targetIdx = orderedWidgets.findIndex((w) => w.id === targetId);
    if (targetIdx === -1) { setDragId(null); return; }
    moveTo(dragId, targetIdx);
    setDragId(null);
  };
  const handleDragEnd = () => setDragId(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dialog-dashboard-settings">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight text-[#0B1D3A] flex items-center gap-2">
            <LayoutGrid size={18} className="text-[#FF5A1F]" />
            Atur Tampilan Dashboard
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Susun ulang urutan (drag &amp; drop atau panah), sembunyikan widget yang tidak dibutuhkan.
            Layout disimpan terpisah untuk masing-masing view (Daily / Weekly / Monthly).
            Default disesuaikan dengan role Anda (<b>{role.toUpperCase()}</b>).
          </DialogDescription>
        </DialogHeader>

        {/* View selector di dialog */}
        <div className="border border-slate-200 bg-slate-50 p-2 flex items-center gap-2" data-testid="settings-view-toggle">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-1">View:</span>
          <div className="inline-flex border border-slate-300 rounded-none overflow-hidden">
            {DASHBOARD_VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                data-testid={`settings-view-${v.id}`}
                onClick={() => setActiveView(v.id)}
                className={"px-3 h-7 text-[10px] uppercase tracking-wider font-semibold transition-colors " + (activeView === v.id ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-100")}
              >
                {v.label}
              </button>
            ))}
          </div>
          <span className="ml-auto text-[10px] text-slate-500 tabular-nums">{eligibleWidgets.length} widget</span>
        </div>

        <div className="border border-slate-200 divide-y divide-slate-100 max-h-[50vh] overflow-y-auto -mx-6 sm:mx-0">
          {orderedWidgets.map((w, idx) => {
            const visible = isVisible(w.id);
            const isFirst = idx === 0;
            const isLast = idx === orderedWidgets.length - 1;
            const isDragging = dragId === w.id;
            return (
              <div
                key={w.id}
                draggable
                onDragStart={handleDragStart(w.id)}
                onDragOver={handleDragOver}
                onDrop={handleDrop(w.id)}
                onDragEnd={handleDragEnd}
                data-testid={`widget-row-${w.id}`}
                className={"flex items-center gap-2 px-3 py-2 transition-all " + (visible ? "" : "bg-slate-50 opacity-70 ") + (isDragging ? "bg-orange-50 border-l-4 border-[#FF5A1F]" : "")}
              >
                <span className="text-slate-300 hover:text-[#FF5A1F] cursor-grab active:cursor-grabbing" title="Drag untuk urut ulang">
                  <GripVertical size={14} />
                </span>
                <span className="text-[10px] font-mono tabular-nums text-slate-400 w-6 text-right">{idx + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className={"text-sm font-semibold " + (visible ? "text-[#0B1D3A]" : "text-slate-500 line-through")}>
                    {w.label}
                    {w.isNew && <span className="ml-1.5 px-1 py-0 text-[8px] font-bold bg-[#FF5A1F] text-white uppercase tracking-wider">Baru</span>}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400">{w.id}</div>
                </div>
                <button
                  type="button"
                  data-testid={`widget-move-up-${w.id}`}
                  onClick={() => moveUp(w.id)}
                  disabled={isFirst}
                  className="p-1 border border-slate-200 text-slate-500 hover:border-[#FF5A1F] hover:text-[#FF5A1F] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Naikkan"
                ><ArrowUp size={12} /></button>
                <button
                  type="button"
                  data-testid={`widget-move-down-${w.id}`}
                  onClick={() => moveDown(w.id)}
                  disabled={isLast}
                  className="p-1 border border-slate-200 text-slate-500 hover:border-[#FF5A1F] hover:text-[#FF5A1F] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Turunkan"
                ><ArrowDown size={12} /></button>
                <button
                  type="button"
                  data-testid={`widget-toggle-${w.id}`}
                  onClick={() => toggleVisibility(w.id)}
                  className={"p-1 border transition-colors " + (visible ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-400 hover:bg-slate-100")}
                  title={visible ? "Sembunyikan widget ini" : "Tampilkan widget ini"}
                >
                  {visible ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="btn-widget-reset-view"
              onClick={resetActiveView}
              className="rounded-none h-8 text-[11px] uppercase tracking-wider flex items-center gap-1"
              title={`Reset view ${activeView} ke default role ${role}`}
            ><RotateCcw size={11} /> Reset View</Button>
            <Button
              type="button"
              variant="outline"
              data-testid="btn-widget-reset-all"
              onClick={reset}
              className="rounded-none h-8 text-[11px] uppercase tracking-wider flex items-center gap-1"
              title="Reset semua view ke default role"
            ><RotateCcw size={11} /> Reset Semua</Button>
          </div>
          <Button
            type="button"
            data-testid="btn-widget-close"
            onClick={() => onOpenChange(false)}
            className="rounded-none h-8 text-[11px] bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase tracking-wider"
          >Selesai</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Wrapper yang respect config per-view: hidden atau not-in-view → null.
export function Widget({ id, children }) {
  const { user } = useAuth();
  const { isVisible, orderOf } = useDashboardConfig({ user, syncBackend: false });
  if (!isVisible(id)) return null;
  return (
    <div style={{ order: orderOf(id) }} data-widget={id}>
      {children}
    </div>
  );
}
